<script setup lang="ts">
import { ref, inject, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Fluid from 'primevue/fluid';
import InputText from 'primevue/inputtext';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const dialogRef = inject('dialogRef') as Ref;
const dataEdit = dialogRef?.value.options.data?.data;
const updateData = dialogRef?.value.options.emits.updateData;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const airManagementStore = useAirManagementStore();

const isLoading = ref(false);
const payload = ref({
  sk: dataEdit?.sk ?? '',
  taxCode: dataEdit?.taxCode ?? '',
  taxType: dataEdit?.taxType ?? '',
  taxDescriptionEn: dataEdit?.taxDescriptionEn ?? '',
  taxDescriptionFr: dataEdit?.taxDescriptionFr ?? ''
});

const rules = {
  taxCode: { required },
  taxDescriptionEn: { required },
  taxDescriptionFr: { required }
};

const v$ = useVuelidate(rules, payload.value);

const submit = async () => {
  v$.value.$validate();
  if (!v$.value.$error) {
    isLoading.value = true;
    if (dataEdit) {
      airManagementStore
        .updateTax(payload.value)
        .then(() => {
          toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
          updateData();
          dialogRef.value.close();
        })
        .finally(() => {
          isLoading.value = false;
        });
    } else {
      airManagementStore
        .addTax(payload.value)
        .then(() => {
          toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
          updateData();
          dialogRef.value.close();
        })
        .finally(() => {
          isLoading.value = false;
        });
    }
  }
};
</script>

<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="manage-tax-modal">
    <Fluid>
      <div class="field grid">
        <div class="col-12 sm:col-6">
          <label for="taxCode">{{ $t('management.airline.tax_code') }} <span class="text-red-500 text-xl">*</span></label>
          <InputText id="taxCode" v-model="payload.taxCode" :class="v$.taxCode.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.taxCode.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col-12 sm:col-6">
          <label for="taxType">{{ $t('management.airline.tax_type') }} <span class="text-xl"></span></label>
          <InputText id="taxType" v-model="payload.taxType" />
        </div>
      </div>
      <div class="field grid">
        <div class="col-12 sm:col-12">
          <label for="taxDescriptionEn">{{ $t('management.airline.tax_description_en') }} <span class="text-red-500 text-xl">*</span></label>
          <InputText id="taxDescriptionEn" v-model="payload.taxDescriptionEn" :class="v$.taxDescriptionEn.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.taxDescriptionEn.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
      <div class="field grid">
        <div class="col-12 sm:col-12">
          <label for="taxDescriptionFr">{{ $t('management.airline.tax_description_fr') }} <span class="text-red-500 text-xl">*</span></label>
          <InputText id="taxDescriptionFr" v-model="payload.taxDescriptionFr" :class="v$.taxDescriptionFr.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.taxDescriptionFr.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <div class="flex justify-content-center mt-3">
      <Button id="submit-btn" :label="$t('voucher.submit')" @click="submit()" />
    </div>
  </div>
</template>
